import {
  ContextType,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  Navigator as BaseNavigator,
  UNSAFE_NavigationContext as NavigationContext,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Blocker, History, Transition } from 'history';

interface Navigator extends BaseNavigator {
  block: History['block'];
}

type NavigationContextWithBlock = ContextType<typeof NavigationContext> & {
  navigator: Navigator;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useBlocker(blocker: Blocker, when = true) {
  const { navigator } = useContext(
    NavigationContext,
  ) as NavigationContextWithBlock;

  useEffect(() => {
    if (!when) {
      return;
    }

    const unblock = navigator.block((tx: Transition) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        },
      };

      blocker(autoUnblockingTx);
    });

    // eslint-disable-next-line consistent-return
    return unblock;
  }, [navigator, blocker, when]);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,import/prefer-default-export
export function usePrompt() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [lastLocation, setLastLocation] = useState<Transition | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [navigationBlocked, setNavigationBlocked] = useState<boolean>(false);

  const cancelNavigation = useCallback(() => {
    setShowPrompt(false);
  }, []);

  const handleBlockedNavigation = useCallback(
    (tx: Transition) => {
      const fixedLocationPathname = tx.location.pathname.replace('/elegislativa-fe', '');
      if (!confirmedNavigation && fixedLocationPathname !== location.pathname) {
        setShowPrompt(true);
        setLastLocation(tx);
        return false;
      }
      return true;
    },
    [confirmedNavigation, location.pathname],
  );

  const confirmNavigation = useCallback(() => {
    setShowPrompt(false);
    setConfirmedNavigation(true);
  }, []);

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      const fixedPathname = lastLocation.location.pathname.replace('/elegislativa-fe', '');
      navigate(fixedPathname);
      window.parent.postMessage({
        type: 'iframe-to-parent-modal-dialogue-close',
      }, '*');
    }
    // eslint-disable-next-line
  }, [confirmedNavigation, lastLocation]);

  useEffect(() => {
    const messageHandler = (message: MessageEvent): void => {
      if (message.data?.type === 'blockNavigation') {
        setNavigationBlocked(message.data.value);
        setConfirmedNavigation(!(message.data.value));
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  useBlocker(handleBlockedNavigation, navigationBlocked);

  return {
    navigationBlocked, showPrompt, confirmNavigation, cancelNavigation,
  };
}
